import { useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Copy, Check, Loader2, RefreshCw, AlertCircle, Users, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";

/**
 * Email signature settings.
 *
 * Everyone gets the same block — the wordmark, their name drawn on as an
 * animated GIF, their title and contact line. Only the personal fields are
 * editable; the layout and company details are fixed server-side so fifteen
 * signatures cannot drift into fifteen designs.
 *
 * The preview is the server's own HTML, not a re-implementation, so what is on
 * screen is exactly what lands on the clipboard.
 */
const EmailSignatureTab = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", title: "", phone: "", email: "" });
  const [signature, setSignature] = useState(null);
  const [template, setTemplate] = useState(null);
  const [copied, setCopied] = useState(false);
  const [needsRepaste, setNeedsRepaste] = useState(false);
  const [dirty, setDirty] = useState(false);

  const isCoo = user?.role === "COO";
  const [team, setTeam] = useState(null);
  const [backfilling, setBackfilling] = useState(false);

  // The preview iframe carries no HQ styles, so it also has no layout telling it
  // how tall to be — measure its content and match.
  const frameRef = useRef(null);
  const [frameH, setFrameH] = useState(140);
  const sizeFrame = useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    if (doc?.body) setFrameH(doc.body.scrollHeight);
  }, []);

  // The GIF plays once and is served immutable, so it is cached after the first
  // view and re-rendering alone will not replay it. Bumping a fragment on the
  // URL counts as a new image to the browser, which restarts the animation
  // without another request.
  const [replay, setReplay] = useState(0);
  const previewHtml = signature
    ? signature.html.replace(signature.asset_url, `${signature.asset_url}#r${replay}`)
    : "";

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get("/email-signature/me");
      setTemplate(res.data.template);
      if (res.data.exists) {
        setSignature(res.data.signature);
        const { name, title, phone, email } = res.data.signature;
        setForm({ name, title, phone: phone || "", email });
      } else {
        setForm({ ...res.data.draft, phone: res.data.draft.phone || "" });
      }
      setDirty(false);
    } catch (e) {
      toast.error("Couldn't load your signature settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTeam = useCallback(async () => {
    try {
      const res = await apiClient.get("/email-signature/team");
      setTeam(res.data);
    } catch (e) {
      /* non-COO or transient — the roster is supplementary, so stay quiet */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (isCoo) loadTeam();
  }, [isCoo, loadTeam]);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setDirty(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Your name is what gets drawn — it can't be blank.");
      return;
    }
    setSaving(true);
    try {
      const res = await apiClient.put("/email-signature/me", {
        name: form.name,
        title: form.title,
        phone: form.phone,
        email: form.email,
      });
      setSignature(res.data.signature);
      setTemplate(res.data.template);
      setNeedsRepaste(res.data.repaste_required);
      setDirty(false);
      toast.success("Signature saved.");
      if (isCoo) loadTeam();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your signature.");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Put the block on the clipboard as rich HTML so a mail client's signature
   * box receives the real markup rather than a wall of tags. The async
   * Clipboard API is refused by Safari and by non-secure origins, so fall back
   * to selecting a live copy of the block and letting the browser copy that.
   */
  const copy = async () => {
    if (!signature?.html) return;
    const plain = [form.name, form.title, template?.company, form.email, form.phone]
      .filter(Boolean)
      .join("\n");
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([signature.html], { type: "text/html" }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        }),
      ]);
    } catch (err) {
      const holder = document.createElement("div");
      holder.innerHTML = signature.html;
      holder.style.cssText = "position:fixed;left:-9999px;top:0;";
      document.body.appendChild(holder);
      const range = document.createRange();
      range.selectNodeContents(holder);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      const ok = document.execCommand("copy");
      sel.removeAllRanges();
      document.body.removeChild(holder);
      if (!ok) {
        toast.error("Your browser blocked the copy. Select the preview and copy it by hand.");
        return;
      }
    }
    setCopied(true);
    setNeedsRepaste(false);
    setTimeout(() => setCopied(false), 2600);
    toast.success("Copied — paste it into your mail client's signature box.");
  };

  const backfill = async () => {
    setBackfilling(true);
    try {
      const res = await apiClient.post("/email-signature/team/backfill");
      const n = res.data.created.length;
      toast.success(
        n
          ? `Created ${n} signature${n === 1 ? "" : "s"}. Everyone can now copy theirs from this tab.`
          : "Everyone already has one."
      );
      if (res.data.failed?.length) {
        toast.error(`${res.data.failed.length} couldn't be generated — check the server log.`);
      }
      loadTeam();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed.");
    } finally {
      setBackfilling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-12">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="body-text">Loading your signature…</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your details</CardTitle>
          <CardDescription>
            The layout, wordmark and company details are the same for everyone. These
            four fields are yours.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="sig-name">Name</Label>
              <Input
                id="sig-name"
                value={form.name}
                onChange={set("name")}
                maxLength={48}
                placeholder="Bernard Essenfeld"
                data-testid="sig-name-input"
              />
              <p className="text-xs text-muted-foreground">This is what gets drawn on.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sig-title">Job title</Label>
              <Input
                id="sig-title"
                value={form.title}
                onChange={set("title")}
                maxLength={64}
                placeholder="Chief Operating Officer"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sig-email">Contact email</Label>
              <Input id="sig-email" value={form.email} onChange={set("email")} maxLength={120} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sig-phone">Phone <span className="text-muted-foreground">(optional)</span></Label>
              <Input
                id="sig-phone"
                value={form.phone}
                onChange={set("phone")}
                maxLength={32}
                placeholder="+1 305 555 0134"
              />
              <p className="text-xs text-muted-foreground">Left out entirely if blank.</p>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <Button onClick={save} disabled={saving || !dirty} data-testid="sig-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {signature ? "Save & re-render" : "Create my signature"}
            </Button>
            {dirty && signature && (
              <span className="text-xs text-muted-foreground">Unsaved changes</span>
            )}
          </div>
        </CardContent>
      </Card>

      {signature && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Preview</CardTitle>
            <CardDescription>
              Exactly what lands on your clipboard. It draws on once, then rests.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Rendered in an iframe, not inline. HQ's global CSS reaches into
                anything injected into the page — its border-color rule carries
                !important and repainted the signature's hairline rule, and its
                border-box reset squeezed the wordmark — so an inline preview
                shows something the recipient will never see. A separate document
                has none of HQ's styles in it, which is the only way this preview
                can honestly claim to be what gets pasted. Also always white,
                whatever theme HQ is in, because that is the mail client. */}
            <div className="rounded-lg border border-border bg-white overflow-x-auto">
              <iframe
                title="Signature preview"
                ref={frameRef}
                srcDoc={`<!doctype html><html><head><meta charset="utf-8"><style>html,body{margin:0;padding:24px;background:#fff;}</style></head><body>${previewHtml}</body></html>`}
                onLoad={sizeFrame}
                scrolling="no"
                style={{ width: "100%", border: 0, display: "block", height: frameH }}
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={copy} data-testid="sig-copy-btn">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copied" : "Copy signature"}
              </Button>
              <Button variant="outline" onClick={() => setReplay((n) => n + 1)}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Replay
              </Button>
            </div>

            {needsRepaste && (
              <div className="flex gap-3 rounded-lg border border-terracotta/30 bg-terracotta/5 p-4">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-terracotta" />
                <div className="space-y-1">
                  <p className="text-sm text-foreground">Paste it again to publish the change.</p>
                  <p className="text-xs text-muted-foreground">
                    Gmail caches signature images and doesn't re-check them, so your
                    already-pasted signature will keep showing the previous version
                    until you replace it.
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-3 pt-1">
              <p className="label-text">How to install it</p>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  <span className="text-foreground">Gmail</span> — Settings → See all
                  settings → General → Signature, then paste and Save changes. Don't use
                  Gmail's own “insert image” button; it re-hosts the file and can flatten
                  the animation.
                </p>
                <p>
                  <span className="text-foreground">Apple Mail</span> — Mail → Settings →
                  Signatures → <strong>+</strong>. Untick “Always match my default message
                  font” first, then paste.
                </p>
                <p>
                  <span className="text-foreground">Outlook</span> — Settings → Mail →
                  Compose and reply, then paste. Outlook on Windows shows only the first
                  frame of a GIF, so it renders your signature finished and static rather
                  than animating — that's expected, not broken.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {isCoo && team && (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Team signatures
                </CardTitle>
                <CardDescription>
                  {team.missing === 0
                    ? "Everyone has one."
                    : `${team.missing} ${team.missing === 1 ? "person" : "people"} without a signature.`}
                </CardDescription>
              </div>
              {team.missing > 0 && (
                <Button variant="outline" onClick={backfill} disabled={backfilling}>
                  {backfilling ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Generate for everyone
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-4">
              “Generate for everyone” seeds a signature from each person's account name
              and role, so they only have to correct it and paste it. It never
              overwrites one somebody has already edited.
            </p>
            <div className="divide-y divide-border">
              {team.users.map((row) => (
                <div key={row.user_id} className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm text-foreground truncate">{row.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {row.title || row.role}
                      {row.title ? "" : " · from role"}
                    </p>
                  </div>
                  {row.has_signature ? (
                    <Badge variant="outline" className="shrink-0">Ready</Badge>
                  ) : (
                    <Badge variant="outline" className="shrink-0 text-muted-foreground">
                      Not set up
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EmailSignatureTab;
